"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  identifyAnalytics,
  resetAnalytics,
  trackAnalytics,
} from "@/lib/analytics/client";
import { AnalyticsEvent, type AuthMethod } from "@/lib/analytics/events";
import { invalidateCache } from "@/lib/api/cache";
import {
  ApiError,
  acceptTokenPair,
  authenticate,
  consumeLoginLink,
  fetchMe,
  logoutRequest,
  oauthApple,
  oauthGoogle,
  register as registerRequest,
  updateMe,
  type AuthUser,
  type MeUpdatePayload,
  type TokenPair,
} from "@/lib/api/auth";
import { clearSessionHint, hasSessionHint } from "@/lib/auth/sessionHint";

function emailFromUser(user: AuthUser): string | null {
  for (const identity of user.identities) {
    const id = identity.identifier?.trim();
    if (id && id.includes("@")) return id;
  }
  return null;
}

function trackAuthSuccess(
  event:
    | typeof AnalyticsEvent.Login
    | typeof AnalyticsEvent.CompleteRegistration,
  method: AuthMethod,
  user: AuthUser,
  emailHint?: string,
) {
  const email = emailFromUser(user) ?? emailHint ?? null;
  identifyAnalytics(String(user.id), { email });
  trackAnalytics(event, { method });
}

type AuthStatus = "checking" | "authenticated" | "guest";

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  /** True once the initial session check (/me) has resolved. */
  hydrated: boolean;
  isAuthenticated: boolean;
  /** True for users with an active paid subscription; false for guests too. */
  isPro: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<void>;
  loginWithApple: (idToken: string) => Promise<void>;
  /** Consume a one-time magic login-link token and establish the cookie session. */
  loginWithLink: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Establish a session from an already-issued token pair
   * (e.g. guest checkout complete that provisioned a new account).
   * Prefer relying on the proxy which parks checkout JWTs into cookies.
   */
  acceptSession: (pair: TokenPair) => Promise<void>;
  /**
   * Mark the current HttpOnly cookie session as authenticated by loading `/me`
   * (e.g. after checkout complete that set cookies server-side).
   */
  adoptCookieSession: () => Promise<void>;
  /** Re-fetches `/me` when a session exists (e.g. after email confirmation). */
  refreshUser: () => Promise<void>;
  /** Persists profile fields via `PATCH /v1/user/me` and updates local user state. */
  updateProfile: (payload: MeUpdatePayload) => Promise<AuthUser>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("checking");

  const establishFromCookies = useCallback(async (): Promise<AuthUser> => {
    // Identity is changing — anything cached belongs to the previous session.
    invalidateCache();
    try {
      const me = await fetchMe();
      setUser(me);
      setStatus("authenticated");
      return me;
    } catch (err) {
      setUser(null);
      setStatus("guest");
      throw err;
    }
  }, []);

  // Boot: drop legacy localStorage JWTs, then probe the cookie session via
  // /me — but only when the session marker says there is one to probe.
  useEffect(() => {
    let cancelled = false;

    try {
      window.localStorage.removeItem("psyday_auth_tokens");
    } catch (err) {
      console.error("Failed to clear legacy auth tokens from localStorage", err);
    }

    (async () => {
      try {
        // No marker cookie → no session to probe; skip the guaranteed 401.
        const me = hasSessionHint() ? await fetchMe() : null;
        if (cancelled) return;
        if (!me) {
          setUser(null);
          setStatus("guest");
          return;
        }
        setUser(me);
        setStatus("authenticated");
      } catch (err) {
        // The marker outlived the session — stop probing on the next load.
        if (err instanceof ApiError && err.status === 401) clearSessionHint();
        if (cancelled) return;
        setUser(null);
        setStatus("guest");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      await authenticate(email, password);
      const me = await establishFromCookies();
      trackAuthSuccess(AnalyticsEvent.Login, "password", me, email);
    },
    [establishFromCookies],
  );

  const register = useCallback(
    async (email: string, password: string) => {
      await registerRequest(email, password);
      const me = await establishFromCookies();
      trackAuthSuccess(
        AnalyticsEvent.CompleteRegistration,
        "password",
        me,
        email,
      );
    },
    [establishFromCookies],
  );

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      await oauthGoogle(idToken);
      const me = await establishFromCookies();
      trackAuthSuccess(AnalyticsEvent.Login, "google", me);
    },
    [establishFromCookies],
  );

  const loginWithApple = useCallback(
    async (idToken: string) => {
      await oauthApple(idToken);
      const me = await establishFromCookies();
      trackAuthSuccess(AnalyticsEvent.Login, "apple", me);
    },
    [establishFromCookies],
  );

  const loginWithLink = useCallback(
    async (token: string) => {
      await consumeLoginLink(token);
      await establishFromCookies();
    },
    [establishFromCookies],
  );

  const acceptSession = useCallback(
    async (pair: TokenPair) => {
      await acceptTokenPair(pair);
      await establishFromCookies();
    },
    [establishFromCookies],
  );

  const adoptCookieSession = useCallback(async () => {
    await establishFromCookies();
  }, [establishFromCookies]);

  const logout = useCallback(async () => {
    setUser(null);
    setStatus("guest");
    // Drop the marker up front: the BFF clears it too, but a failed request
    // must not leave the next boot probing a session that is already gone.
    clearSessionHint();
    // Never serve the signed-out user cached rows from the previous session.
    invalidateCache();
    resetAnalytics();
    try {
      await logoutRequest();
    } catch (err) {
      // Best-effort: local session is already cleared.
      console.error("Logout request failed", err);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const me = await fetchMe();
      setUser(me);
      setStatus("authenticated");
    } catch (err) {
      console.error("Failed to refresh user", err);
    }
  }, []);

  const updateProfile = useCallback(
    async (payload: MeUpdatePayload): Promise<AuthUser> => {
      try {
        const me = await updateMe(payload);
        setUser(me);
        setStatus("authenticated");
        return me;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          clearSessionHint();
          setUser(null);
          setStatus("guest");
        }
        throw err;
      }
    },
    [],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      hydrated: status !== "checking",
      isAuthenticated: status === "authenticated",
      isPro: status === "authenticated" && user?.subscription?.isActive === true,
      login,
      register,
      loginWithGoogle,
      loginWithApple,
      loginWithLink,
      acceptSession,
      adoptCookieSession,
      logout,
      refreshUser,
      updateProfile,
    }),
    [
      user,
      status,
      login,
      register,
      loginWithGoogle,
      loginWithApple,
      loginWithLink,
      acceptSession,
      adoptCookieSession,
      logout,
      refreshUser,
      updateProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}

export { ApiError };
